import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { Config } from 'datatables.net';
import { Subject } from 'rxjs';
import { NumberDirective } from '../../directives/number.directive';
import { Invoice } from '../../models/invoice';
import { Payment } from '../../models/payment';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ModalService } from '../../services/modal/modal.service';
import { PaymentService } from '../../services/payment/payment.service';
import { Utils } from '../../utils/utils';

@Component({
  selector: 'app-payments',
  imports: [
    DataTablesModule,
    ReactiveFormsModule,
    CommonModule,
    NumberDirective,
  ],
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss',
})
export class PaymentsComponent implements OnInit, AfterViewInit {
  error?: string;
  form!: FormGroup;
  submitting: boolean = false;
  invoiceId!: number;
  invoice!: Invoice;
  deleteIndex: number = 0;
  fullyLoaded: boolean = false;

  dtOptions!: Config;

  @ViewChild(DataTableDirective)
  datatableElement!: DataTableDirective;
  dtTrigger: Subject<any> = new Subject<any>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private modalService: ModalService,
    private vcr: ViewContainerRef,
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef,
    private changeDetector: ChangeDetectorRef
  ) {
    this.form = fb.group({
      invoiceId: fb.control(null, Validators.required),
      time: fb.control(Utils.now(), Validators.required),
      date: fb.control(Utils.today(), Validators.required),
      description: fb.control(''),
      amount: fb.control(null, Validators.required),
      payments: fb.array([]),
      toBeDeleted: fb.array([]),
      paidAmount: fb.control(0),
    });
  }

  ngOnInit(): void {
    this.invoiceId =
      Number(this.route.snapshot.paramMap.get('invoiceId')) || -1;

    this.form.get('invoiceId')?.setValue(this.invoiceId);

    this.paymentService
      .getPaymentsByInvoiceId(this.invoiceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.invoice = resp.invoice;

          this.form.get('paidAmount')?.setValue(this.invoice.paidAmount);

          let payments: FormArray = this.form.get('payments') as FormArray;
          resp.payments.forEach((p: Payment) => {
            payments.push(
              this.fb.group({
                id: this.fb.control(p.id),
                amount: this.fb.control(p.amount, Validators.required),
                date: this.fb.control(p.date, Validators.required),
                description: this.fb.control(p.description),
              })
            );
          });

          this.fullyLoaded = true;
          this.changeDetector.detectChanges();
          this.dtTrigger.next(null);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.submitting = false;
            this.modalService.showErrorDialog(this.vcr, err.error.message);
            this.router.navigateByUrl('dashboard/invoices');
          }
        },
      });

    this.form
      .get('amount')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value: string) => {
          this.amountMonitor();
        },
      });

    this.dtOptions = {
      paging: false,
      searching: false,
      destroy: true,
      info: false,
      ajax: (dataTablesParameters: any, callback) => {
        callback({ data: this.form.get('payments')?.value });
      },
      lengthMenu: [5, 10, 20, 50],
      columns: [
        {
          title: 'Date',
          data: 'date',
        },
        {
          title: 'Description',
          data: 'description',
        },
        {
          title: 'Amount',
          data: 'amount',
        },
        {
          title: 'Action',
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            return `<a href="javascript: void(0);" id="delete" class="action-icon"> <i class="mdi mdi-delete"></i></a>`;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | object, index: number) => {
        let deleteIcon: JQuery<HTMLElement> = $('td:last-child #delete', row);

        if (!(data as Payment).id) {
          (data as any).deleteIndex = this.deleteIndex++;
        }

        deleteIcon.off('click');
        deleteIcon.on('click', () => this.deletePayment(data as Payment));
      },
    };
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
  }

  addPayment() {
    if (this.form.valid) {
      let payments: FormArray = this.form.get('payments') as FormArray;

      let dateTime: string =
        this.form.get('date')?.value + ' ' + this.form.get('time')?.value;

      payments.push(
        this.fb.group({
          invoiceId: this.fb.control(
            this.form.get('invoiceId')?.value,
            Validators.required
          ),
          amount: this.fb.control(
            this.form.get('amount')?.value,
            Validators.required
          ),
          date: this.fb.control(dateTime, Validators.required),
          description: this.fb.control(this.form.get('description')?.value),
        })
      );

      let ex = Number(this.form.get('paidAmount')?.value);
      this.form
        .get('paidAmount')
        ?.setValue(ex + Number(this.form.get('amount')?.value));

      this.form.get('amount')?.reset();
      this.form.get('description')?.reset();
      this.form.get('date')?.setValue(Utils.today());
      this.form.get('time')?.setValue(Utils.now());

      this.dtTrigger.next(null);
    }
  }

  deletePayment(data: Payment) {
    let payments: FormArray = this.form.get('payments') as FormArray;
    let index = -1;

    if (data.id) {
      (this.form.get('toBeDeleted') as FormArray).push(
        this.fb.control(Number(data.id))
      );

      index = payments.value.findIndex((p: Payment) => p.id === data.id);
      payments.removeAt(index);
    } else {
      let deleteIndex = (data as any).deleteIndex;
      index = payments.value.findIndex(
        (p: any) => p?.deleteIndex === deleteIndex
      );
      payments.removeAt(index);
    }

    let ex = Number(this.form.get('paidAmount')?.value);
    this.form.get('paidAmount')?.setValue(ex - data.amount);

    this.dtTrigger.next(null);
  }

  savePayments() {
    this.error = undefined;
    this.submitting = true;
    this.paymentService
      .updatePaymentByInvoiceId(
        this.invoiceId,
        this.form.get('payments')?.value,
        this.form.get('toBeDeleted')?.value
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.modalService.showSuccessDialog(
            this.vcr,
            'Payments has been saved.'
          );
          this.router.navigateByUrl('dashboard/invoices');
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.submitting = false;
            this.modalService.showErrorDialog(this.vcr, err.error.message);
          }
        },
      });
  }

  amountMonitor() {
    let paidAmount: number = Number(this.form.get('paidAmount')?.value);
    let currentAmount: number = Number(this.form.get('amount')?.value);
    if (paidAmount + currentAmount > this.invoice.amount) {
      this.form.get('amount')?.setValue(this.invoice.amount - paidAmount);
    }
  }
}
